import { AssessmentPeriodRead, portalApi } from '../../../store/generated/portal/assessments';
import { ASSESSMENT_PERIOD_IN_PROGRESS, ASSESSMENT_PERIOD_OPEN } from '../constants';
import { DebriefScheduleApiTags } from './debriefScheduleApi';
import { UserContextApiTags } from '../../../common/api/userContextApi';

export enum AssessmentPeriodApiTags {
  AssessmentPeriod = 'AssessmentPeriod',
  DebriefSchedules = DebriefScheduleApiTags.DebriefSchedules,
}

// Manually inject send_reminders endpoint until API generator runs
const apiWithReminders = portalApi.injectEndpoints({
  endpoints: (build) => ({
    apiPortalV1AssessmentsSendRemindersCreate: build.mutation<
      { success: boolean; count: number; message?: string; error?: string },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/api/portal/v1/assessments/${id}/send_reminders/`,
        method: 'POST',
      }),
    }),
  }),
  overrideExisting: false,
});

const assessmentPeriodApi = apiWithReminders.enhanceEndpoints({
  addTagTypes: [
    AssessmentPeriodApiTags.AssessmentPeriod,
    AssessmentPeriodApiTags.DebriefSchedules,
    UserContextApiTags.UserContext,
    UserContextApiTags.OrganizationContext,
  ],
  endpoints: {
    apiPortalV1AssessmentsList: {
      providesTags: [AssessmentPeriodApiTags.AssessmentPeriod],
    },
    apiPortalV1AssessmentsCreate: {
      invalidatesTags: [
        AssessmentPeriodApiTags.AssessmentPeriod,
        AssessmentPeriodApiTags.DebriefSchedules,
        UserContextApiTags.UserContext,
        UserContextApiTags.OrganizationContext,
      ],
    },
    apiPortalV1AssessmentsRetrieve: {
      providesTags: (result, error, arg) => [
        { type: AssessmentPeriodApiTags.AssessmentPeriod, id: arg.id },
      ],
    },
    apiPortalV1AssessmentsUpdate: {
      invalidatesTags: [
        AssessmentPeriodApiTags.AssessmentPeriod,
        AssessmentPeriodApiTags.DebriefSchedules,
        UserContextApiTags.UserContext,
        UserContextApiTags.OrganizationContext,
      ],
    },
    apiPortalV1AssessmentsPartialUpdate: {
      invalidatesTags: [
        AssessmentPeriodApiTags.AssessmentPeriod,
        AssessmentPeriodApiTags.DebriefSchedules,
        UserContextApiTags.UserContext,
        UserContextApiTags.OrganizationContext,
      ],
    },
    apiPortalV1AssessmentsDestroy: {
      invalidatesTags: [
        AssessmentPeriodApiTags.AssessmentPeriod,
        AssessmentPeriodApiTags.DebriefSchedules,
        UserContextApiTags.UserContext,
        UserContextApiTags.OrganizationContext,
      ],
    },
    apiPortalV1AssessmentsCloseCreate: {
      invalidatesTags: [
        AssessmentPeriodApiTags.AssessmentPeriod,
        UserContextApiTags.UserContext,
        UserContextApiTags.OrganizationContext,
      ],
    },
    apiPortalV1AssessmentsCompletedRetrieve: {
      providesTags: [AssessmentPeriodApiTags.AssessmentPeriod],
    },
    apiPortalV1AssessmentsSendRemindersCreate: {
      invalidatesTags: [
        AssessmentPeriodApiTags.AssessmentPeriod,
        AssessmentPeriodApiTags.DebriefSchedules,
        UserContextApiTags.UserContext,
        UserContextApiTags.OrganizationContext,
      ],
    },
  },
});

const getMostRecentPeriod = (assessmentPeriods: AssessmentPeriodRead[]): AssessmentPeriodRead | undefined => {
  if (!assessmentPeriods?.length) return undefined;
  return assessmentPeriods.reduce((mostRecent, p) => {
    const mostTime = mostRecent.start_date_time ? new Date(mostRecent.start_date_time).getTime() : 0;
    const pTime = p.start_date_time ? new Date(p.start_date_time).getTime() : 0;
    return pTime > mostTime ? p : mostRecent;
  }, assessmentPeriods[0]);
};

export const getMostRecentOpenPeriod = (assessmentPeriods: AssessmentPeriodRead[]): AssessmentPeriodRead | undefined => {
  if (!assessmentPeriods?.length) return undefined;

  // First, try to find the most recent open period
  const openPeriods = assessmentPeriods
    .filter(period => period.status === ASSESSMENT_PERIOD_OPEN || period.status === ASSESSMENT_PERIOD_IN_PROGRESS)
    .sort(
      (a, b) =>
        new Date(b.start_date_time).getTime() - new Date(a.start_date_time).getTime()
    );

  if (openPeriods.length > 0) {
    return openPeriods[0]; // Return the most recent open period
  }

  // If no open periods, fall back to the most recent period overall
  return getMostRecentPeriod(assessmentPeriods);
};

// Export the query hook - Culture Hub needs to fetch assessment periods by scope (organization or team)
export const useGetAssessmentPeriodsByScopeQuery = assessmentPeriodApi.useApiPortalV1AssessmentsListQuery;

export { assessmentPeriodApi };

